import bigDecimal from 'js-big-decimal'
import moment from 'moment'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

import { BasicType } from '../interfaces/InterfacesLocal'
import {
  AllStatusDatesInterfaces,
  AreaResourcesInterface,
  AreasInterface,
  BatchItemInterface,
  BusinessInterface,
  DispatchItemInterface,
  DispatchStatus,
  Inventories,
  Movement,
  OperationalCostInterface,
  PaginateInterface,
  PaymentReceiptsInterface,
  ProductInterface,
  ProductMovementsInterface,
  ReceiptBatchInterface,
  ReceiptDocsInterface,
  ShareArea,
  StockAreaProducts,
  StockInventoryReport,
  StockReport,
  SummarizePaymentInterface,
} from '../interfaces/ServerInterfaces'
import CargoReportPDF from '../reports/CargoReportPDF'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  updateAreaAfterDelete,
  updateAreas,
  updateCurrentArea,
} from '../store/slices/nomenclatorSlice'
import { printPriceWithCommasAndPeriods } from '../utils/functions'
import { exportExcel, generatePdf, generateUrlParams } from '../utils/helpers'
import { translateMeasure, translateProductTypes } from '../utils/translate'
import query from './APIServices'
import useServer from './useServerMain'
import { CreateDispatchPayload } from '../interfaces/dispatch'
import { getStockAreasForDispatch, getProductsByAreaCached } from './areasAPI'

interface ApiError {
  status?: number
  response?: {
    data?: {
      message?: string
      error?: string
      errors?: Record<string, string[]>
    }
  }
  message?: string
}

// Additional interfaces for type safety
interface MovementElement {
  product: ProductInterface
  quantity: number
  variation: {
    id: number
    name: string
  } | null
}

// Interface for quick dispatch product list (used as `ListProduc` in acceptDispatch)
// Fields chosen conservatively to match typical dispatch payloads: product id, quantity
// and an optional variation id when the product has variations.
interface ListProduct {
  productId: number
  quantity: number
  variationId?: number | null
}

type ListProduc = ListProduct[]

type ExportDataObject = Record<string, string | number>

type ReceiptDocumentsPayload = Record<string, string | number | boolean | null>

type OperationalCostData = Record<string, string | number | boolean | null>

type BatchData = Record<string, string | number | boolean | null>

type ReceiptData = Record<string, string | number | boolean | null>

type ProductMovementData = Record<string, string | number | boolean | null>

interface StatusDateData {
  registeredAt: string
}

const useServerArea = () => {
  const dispatch = useAppDispatch()
  const { allowRoles } = useServer()
  const { business } = useAppSelector((state) => state.init)
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loading, setLoading] = useState<DispatchStatus>()
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [paginate, setPaginate] = useState<PaginateInterface | null>(null)
  const [allAreas, setAllAreas] = useState<AreasInterface[]>([])
  const [areaAllFilter, setAreaAllFilter] = useState<AreasInterface[]>([])
  const [area, setArea] = useState<AreasInterface | null>(null) //current Area
  const [allMovements, setAllMovements] = useState<Movement[]>([])
  const [movement, setMovement] = useState<Movement | null>(null)
  const [allInventories, setAllInventories] = useState<Inventories[]>([])
  const [inventory, setInventory] = useState<Inventories | null>(null)
  const [allDispatches, setAllDispatches] = useState<DispatchItemInterface[]>(
    []
  )
  const [allBatches, setAllBatches] = useState<BatchItemInterface[]>([])
  const [allOperationalCosts, setAllOperationalCosts] = useState<
    OperationalCostInterface[]
  >([])
  const [stockProducts, setStockProducts] = useState<StockAreaProducts[]>([]) //Products of current Area
  const [despacho, setDespacho] = useState<DispatchItemInterface | null>(null)
  const [sharesAreas, setSharesAreas] = useState<AreasInterface[]>([])
  const [sharesAreasConfiguration, setSharesAreasConfiguration] = useState<
    ShareArea[]
  >([])
  const [areaResources, setAreaResources] = useState<AreaResourcesInterface[]>(
    []
  )
  const [quickProductReport, setQuickProductReport] =
    useState<StockReport | null>(null)
  const [stockInvestmentReport, setStockInvestmentReport] =
    useState<StockInventoryReport | null>(null)

  const [pendingDispatchCount, setPendingDispatchCount] = useState(0)
  const [receiptDocs, setReceiptDocs] = useState<ReceiptDocsInterface[]>([])

  const [receiptList, setReceiptList] = useState<ReceiptBatchInterface[]>([])
  const [receipt, setReceipt] = useState<ReceiptBatchInterface | null>(null)
  const [receiptPayments, setReceiptPayments] = useState<
    PaymentReceiptsInterface[] | null
  >(null)

  const [productMovements, setproductMovements] = useState<
    ProductMovementsInterface[] | null
  >(null)

  const [allStatusDates, setAllStatusDates] = useState<
    AllStatusDatesInterfaces[] | null
  >(null)

  const [summaraizedPayments, setSummaraizedPayments] = useState<
    SummarizePaymentInterface[] | null
  >(null)

  const { manageErrors } = useServer()

  const updateStockProductState = (product: ProductInterface) => {
    const newProdArray = [...stockProducts]
    const index = newProdArray.findIndex(
      (value) => value.product.id === product.id
    )
    if (index !== -1)
      newProdArray.splice(index, 1, { ...newProdArray[index], product })
    setStockProducts(newProdArray)
  }

  const getProductsByArea = useCallback(
    async (
      areaId?: string | number | null,
      filter?: Record<string, string | number | boolean | null>
    ) => {
      if (!areaId) {
        setStockProducts([])
        return
      }

      setIsLoading(true)
      try {
        // Use cached function from areasAPI
        const response = await getProductsByAreaCached(areaId, {
          all_data: true,
          search: filter?.search ? String(filter.search) : undefined,
        })

        setStockProducts(response.items)
        setPaginate({
          totalItems: response.totalItems,
          totalPages: response.totalPages,
          currentPage: response.currentPage,
        })
      } catch (e) {
        manageErrors(e as ApiError)
      }
      setIsLoading(false)
    },
    [manageErrors]
  )

  const getAllAreas = async (filter?: BasicType, skeepCondition?: boolean) => {
    if (allAreas.length === 0 || skeepCondition === true) {
      setIsLoading(true)
      await query
        .get(`/administration/area${generateUrlParams(filter)}`)
        .then((resp) => {
          setAllAreas(resp.data.items)
          setPaginate({
            currentPage: resp.data.currentPage,
            totalItems: resp.data.totalItems,
            totalPages: resp.data.totalPages,
          })
        })
        .catch((e) => manageErrors(e))
      setIsLoading(false)
    }
  }

  const getAreaAllFinder = async (
    filter?: BasicType,
    skeepCondition?: boolean
  ) => {
    if (areaAllFilter.length === 0 || skeepCondition === true) {
      setIsLoading(true)
      await query
        .get(`/administration/area/all/finder${generateUrlParams(filter)}`)
        .then((resp) => {
          setAreaAllFilter(resp.data)
        })
        .catch((e) => manageErrors(e))
      setIsLoading(false)
    }
  }

  const getAreasToDispatch = useCallback(async () => {
    setIsLoading(true)
    try {
      // Use cached function from areasAPI
      const { stockAreas, sharedAreas } = await getStockAreasForDispatch()

      // Update local state
      setAllAreas(stockAreas)
      setSharesAreas(sharedAreas)
    } catch (e) {
      manageErrors(e as ApiError)
    }
    setIsLoading(false)
  }, [manageErrors])

  const getSharesAreas = async () => {
    setIsLoading(true)
    await query
      .get('/administration/sharedarea')
      .then((resp) => setSharesAreas(resp.data.items))
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const getArea = async (id: number) => {
    setIsLoading(true)
    await query
      .get(`/administration/area/${id}`)
      .then((resp) => {
        setArea(resp.data)
      })
      .catch((e) => {
        manageErrors(e)
      })
    setIsLoading(false)
  }

  const addArea = async (data: BasicType, closeModal: () => void) => {
    setIsFetching(true)
    await query
      .post('/administration/area', data)
      .then((resp) => {
        dispatch(updateAreas(resp.data))
        setAllAreas([resp.data, ...allAreas])
        closeModal()
        toast.success('Área insertada correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const deleteArea = async (id: string, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/area/${id}`, {})
      .then(() => {
        dispatch(updateAreaAfterDelete(id))
        toast.success('Se elimino el área correctamente')
        callback()
      })
      .catch((error) => manageErrors(error))
    setIsFetching(false)
  }

  const updateArea = async (
    id: number,
    data: Record<string, string | number | boolean | (string | number)[]>,
    closeModal?: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/area/${id}`, data)
      .then((resp) => {
        setArea(resp.data)
        dispatch(updateCurrentArea(resp.data))
        toast.success('Se actualizó el área correctamente')
        if (closeModal) {
          closeModal()
        }
      })
      .catch((e) => {
        manageErrors(e)
      })
    setIsFetching(false)
  }

  const getAreaResources = (id: string) => {
    setIsLoading(true)
    const promise = query.get(`/administration/resource/${id}`)
    promise
      .then((resp) => {
        setAreaResources(resp.data.items)
        setPaginate({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
        setIsLoading(false)
      })
      .catch((e) => {
        setIsLoading(false)
        manageErrors(e)
      })
  }

  const addAreaResource = (
    areaId: string,
    data: Record<string, string | number | boolean>,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    const promise = query.post(`/administration/resource/${areaId}`, data)
    promise
      .then((resp) => {
        setAreaResources([resp.data, ...areaResources])
        setIsFetching(false)
        closeModal()
      })
      .catch((e) => {
        manageErrors(e)
        setIsFetching(false)
      })
    toast.promise(promise, {
      success: 'Recurso creado correctamente',
      pending: `Creando ${data.code} ...`,
    })
  }

  const updateAreaResource = (
    id: number,
    data: Record<string, string | number | boolean>,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    const promise = query.patch(`/administration/resource/${id}`, data)
    promise
      .then((resp) => {
        const resources = areaResources
        const updElem = resources.findIndex((item) => item.id === id)
        resources.splice(updElem, 1, resp.data)
        setAreaResources(resources)
        closeModal()
        setIsFetching(false)
      })
      .catch((e) => {
        manageErrors(e)
        setIsFetching(false)
      })
    toast.promise(promise, {
      pending: 'Actualizando el recurso, por favor espere ...',
      success: 'Recurso actualizado',
    })
  }

  const deleteAreaResource = (id: string, closeModal: () => void) => {
    setIsFetching(true)
    const promise = query.deleteAPI(`/administration/resource/${id}`, {})
    promise
      .then(() => {
        setAreaResources(areaResources.filter((item) => item.id !== Number(id)))
        setIsFetching(false)
        closeModal()
      })
      .catch((e) => manageErrors(e))
    toast.promise(promise, {
      pending: 'Eliminando recurso ...',
      success: 'Recurso eliminado con éxito.',
    })
  }

  const getMovementByArea = async (
    filter?: Record<string, string | boolean | number | null>
  ) => {
    setIsLoading(true)
    await query
      .get(`/administration/movement${generateUrlParams(filter)}`)
      .then((resp) => {
        setAllMovements(resp.data.items)
        setPaginate({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
      })
      .catch((e) => manageErrors(e))

    setIsLoading(false)
  }

  const getInventories = async (
    filter?: Record<string, string | boolean | number | null>
  ) => {
    setIsLoading(true)
    await query
      .get(`/administration/movement/v2/inventory${generateUrlParams(filter)}`)
      .then((resp) => {
        setAllInventories(resp.data.items)
        setPaginate({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
      })
      .catch((e) => manageErrors(e))

    setIsLoading(false)
  }

  const getMovement = async (id: number) => {
    setIsLoading(true)
    await query
      .get(`/administration/movement/${id}`)
      .then((resp) => {
        setMovement(resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const getInventory = async (id: number) => {
    setIsLoading(true)
    await query
      .get(`/administration/movement/v2/inventory/${id}`)
      .then((resp) => {
        setInventory(resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const addMovement = async (
    type: string,
    data: Record<string, string | number | boolean | (string | number)[]>,
    closeModal: () => void
  ) => {
    const typeToApi = type === 'MOVEMENT' ? 'move' : type?.toLocaleLowerCase()
    setIsFetching(true)
    await query
      .post(`/administration/movement/bulk/${typeToApi}`, data)
      .then((resp) => {
        const newStock = [...stockProducts]
        let newCost = quickProductReport?.total_cost.amount ?? 0
        let newSale = quickProductReport?.total_estimated_sales.amount ?? 0
        let newTotalType = quickProductReport?.total_products_type ?? 0
        resp.data.forEach((element: MovementElement) => {
          const index = newStock.findIndex(
            (item) => item.product.id === element.product.id
          )
          let priceAmount = 0
          const price = element.product.prices.find((price) => price.isMain)
          if (price) {
            if (price.codeCurrency === business?.costCurrency) {
              priceAmount = price.price
            } else {
              const exchangeRate = business?.availableCurrencies.find(
                (item) => item.code === price?.codeCurrency
              )?.exchangeRate

              if (!exchangeRate) {
                toast.error('No se encontró la tasa de cambio')
                return
              }
              priceAmount = price.price * exchangeRate
            }
          }
          if (index !== -1) {
            let quantity: number = newStock[index].quantity
            const variationIndex = newStock[index]?.variations?.findIndex(
              (itm) => itm.variationId === element?.variation?.id
            )
            let variationQuantity =
              variationIndex !== -1
                ? newStock[index].variations[variationIndex].quantity
                : element.variation !== null
                  ? 0
                  : undefined
            if (type === 'MOVEMENT') {
              quantity -= element.quantity
              if (variationQuantity) variationQuantity -= element.quantity
              newCost -= element.quantity * newStock[index].product.averageCost
              newSale -= element.quantity * priceAmount
              if (quantity === 0) newTotalType--
            } else {
              quantity += element.quantity
              if (variationQuantity !== undefined)
                variationQuantity += element.quantity
              newCost += element.quantity * element.product.averageCost
              newSale += element.quantity * priceAmount
              if (quantity === 0) newTotalType--
            }
            newStock.splice(index, 1, {
              ...newStock[index],
              quantity,
            })
            if (variationIndex !== -1 && variationQuantity !== undefined) {
              const currentVar = newStock[index].variations[variationIndex]
              newStock[index].variations.splice(variationIndex, 1, {
                ...currentVar,
                quantity: variationQuantity,
              })
            } else if (variationQuantity !== undefined && element.variation) {
              newStock[index].variations.push({
                id: 0,
                variationId: element.variation.id,
                quantity: element.quantity,
                variation: {
                  id: element.variation.id,
                  name: element.variation.name,
                  attributes: [],
                  description: '',
                  image: [],
                  onSale: false,
                  onSalePrice: null,
                  price: null,
                },
              })
            }
          } else {
            newTotalType++
            newCost += element.quantity * element.product.averageCost
            newSale += element.quantity * priceAmount
            const variation = element.variation
              ? [
                  {
                    id: 0,
                    variationId: element.variation.id,
                    quantity: element.quantity,
                    variation: {
                      id: element.variation.id,
                      name: element.variation.name,
                      description: '',
                      onSale: false,
                      price: null,
                      onSalePrice: null,
                      image: [],
                      attributes: [],
                    },
                  },
                ]
              : []
            newStock.push({
              id: 0,
              quantity: element.quantity,
              product: element.product,
              variations: variation,
              area: { id: 0, name: '' },
            })
          }
        })
        setStockProducts(newStock.filter((itm) => itm.quantity !== 0))

        //Report---------------------------------------------------------
        if (quickProductReport) {
          setQuickProductReport({
            total_products_type: newTotalType,
            total_cost: { ...quickProductReport.total_cost, amount: newCost },
            total_estimated_sales: {
              ...quickProductReport.total_estimated_sales,
              amount: newSale,
            },
            total_estimated_profits: {
              ...quickProductReport.total_estimated_profits,
              amount: newSale - newCost,
            },
          })
        }
        //------------------------------------------------------------------
        toast.success('Operación realizada con éxito')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const deleteMovements = async (
    areaId: number | string,
    dateEnd: string,
    closeModal?: () => void
  ) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/bulk-movement/delete`, { dateEnd, areaId })
      .then(() => {
        if (closeModal) {
          closeModal()
        }
        toast.success('Operaciones eliminadas con éxito')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const deleteStockMovement = (
    id: number,
    data: { description: string },
    closeModal: () => void
  ) => {
    setIsFetching(true)
    query
      .deleteAPI(`/administration/movement/${id}`, data)
      .then(() => {
        setIsFetching(false)
        closeModal()
      })
      .catch((e) => {
        setIsFetching(false)
        toast.error(
          e.response?.data?.message ?? 'Error al eliminar la operación'
        )
      })
  }

  const getAllDispatches = async (
    filter?: Record<string, string | number | boolean | null>
  ) => {
    setIsLoading(true)
    await query
      .get(`/administration/dispatch${generateUrlParams(filter)}`)
      .then((resp) => {
        setAllDispatches(resp.data.items)
        setPaginate({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
      })
      .catch((e) => manageErrors(e))

    setIsLoading(false)
  }

  const getAllDispatchesV2 = async (
    filter?: Record<string, string | number | boolean | null>
  ) => {
    setIsLoading(true)
    try {
      const resp = await query.get(
        `/administration/dispatch/movement/all${generateUrlParams(filter)}`
      )
      setAllDispatches(resp.data.items)
      setPaginate({
        currentPage: resp.data.currentPage,
        totalItems: resp.data.totalItems,
        totalPages: resp.data.totalPages,
      })
      setIsLoading(false)
      return resp.data
    } catch (e) {
      manageErrors(e as ApiError)
      setIsLoading(false)
      return null
    }
  }

  const addDispatchAndUpdatePreDispatchState = async (
    id: number,
    data: DispatchItemInterface,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await Promise.all([
      query.post('/administration/dispatch/v3', data),
      query.patch(`/administration/predispatch/${id}`, {
        status: 'ACCEPTED',
      }),
    ])
      .then((resp) => {
        const idx = allDispatches.findIndex((item) => item.id === id)
        const dipatch = allDispatches.find((item) => item.id === id)
        const updated = allDispatches

        const updatedDispacth = {
          ...dipatch,
          status: 'ACCEPTED' as DispatchStatus,
        } as DispatchItemInterface

        updated.splice(idx, 1, updatedDispacth)

        setAllDispatches([resp[0].data, ...updated])
        toast.success('Operación realizada exitosamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const addDispatch = async (
    data: DispatchItemInterface,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await query
      // .post("/administration/dispatch", data)
      .post('/administration/dispatch/v3', data)
      .then((resp) => {
        setAllDispatches([resp.data, ...allDispatches])
        toast.success('Operación realizada exitosamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const getDispatch = async (id: number) => {
    setIsLoading(true)
    await query
      .get('/administration/dispatch/' + id)
      .then((resp) => setDespacho(resp.data))
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const acceptDispatch = async (
    id: number,
    isPreDispatch: boolean,
    closeModal: () => void,
    refetchList?: () => void
  ) => {
    setLoading('ACCEPTED')

    try {
      const endpoint = `/administration/dispatch/movement/${id}/accept`
      const syncId = `accept-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      const listProducts = [] as ListProduc

      const payload = { isPreDispatch, syncId, listProducts }

      await query.post(endpoint, payload)

      const idx = allDispatches.findIndex((item) => item.id === id)

      if (idx !== -1) {
        const updatedDispatches = [...allDispatches]
        const updatedDispatch: DispatchItemInterface = {
          ...updatedDispatches[idx],
          status: 'ACCEPTED',
        }

        updatedDispatches.splice(idx, 1, updatedDispatch)
        setAllDispatches(updatedDispatches)
      }

      toast.success('Despacho aceptado exitosamente.')

      // Invalite cache inventary and batch
      queryClient.invalidateQueries({ queryKey: ['containers-products'] })
      queryClient.invalidateQueries({ queryKey: ['product-details'] })

      if (refetchList) {
        refetchList()
      }
      closeModal()
    } catch (e) {
      manageErrors(e as { message: string })
    } finally {
      setLoading(undefined)
    }
  }

  const rejectDispatch = async (
    id: number,
    isPreDispatch: boolean,
    closeModal: () => void,
    refetchList?: () => void
  ) => {
    setLoading('REJECTED')

    try {
      const endpoint = `/administration/dispatch/movement/${id}/reject`
      const syncId = `reject-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      const listProducts = [] as ListProduc

      const payload = { isPreDispatch, syncId, listProducts }

      await query.post(endpoint, payload)

      const idx = allDispatches.findIndex((item) => item.id === id)

      if (idx !== -1) {
        const updatedDispatches = [...allDispatches]
        const updatedDispatch: DispatchItemInterface = {
          ...updatedDispatches[idx],
          status: 'REJECTED',
        }

        updatedDispatches.splice(idx, 1, updatedDispatch)
        setAllDispatches(updatedDispatches)
      }

      toast.success('Despacho rechazado exitosamente.')

      if (refetchList) {
        refetchList()
      }
      closeModal()
    } catch (e) {
      manageErrors(e as { message: string })
    } finally {
      setLoading(undefined)
    }
  }

  const acceptPreDispatch = async (
    id: number,
    closeModal: () => void,
    refetchList?: () => void
  ) => {
    setLoading('ACCEPTED')

    try {
      const endpoint = `/administration/predispatch/${id}`
      const payload = { status: 'ACCEPTED' }

      await query.patch(endpoint, payload)

      const idx = allDispatches.findIndex((item) => item.id === id)

      if (idx !== -1) {
        const updatedDispatches = [...allDispatches]
        const updatedDispatch: DispatchItemInterface = {
          ...updatedDispatches[idx],
          status: 'ACCEPTED',
        }

        updatedDispatches.splice(idx, 1, updatedDispatch)
        setAllDispatches(updatedDispatches)
      }

      toast.success('Predespacho aceptado exitosamente.')

      queryClient.invalidateQueries({ queryKey: ['containers-products'] })
      queryClient.invalidateQueries({ queryKey: ['product-details'] })

      if (refetchList) {
        refetchList()
      }
      closeModal()
    } catch (e) {
      manageErrors(e as { message: string })
    } finally {
      setLoading(undefined)
    }
  }

  const rejectPreDispatch = async (
    id: number,
    closeModal: () => void,
    refetchList?: () => void
  ) => {
    setLoading('REJECTED')

    try {
      const endpoint = `/administration/predispatch/${id}`
      const payload = { status: 'REJECTED' }

      await query.patch(endpoint, payload)

      const idx = allDispatches.findIndex((item) => item.id === id)

      if (idx !== -1) {
        const updatedDispatches = [...allDispatches]
        const updatedDispatch: DispatchItemInterface = {
          ...updatedDispatches[idx],
          status: 'REJECTED',
        }

        updatedDispatches.splice(idx, 1, updatedDispatch)
        setAllDispatches(updatedDispatches)
      }

      toast.success('Predespacho rechazado exitosamente.')

      if (refetchList) {
        refetchList()
      }
      closeModal()
    } catch (e) {
      manageErrors(e as { message: string })
    } finally {
      setLoading(undefined)
    }
  }

  const responseDispatch = async (
    id: number,
    response: DispatchStatus,
    closeModal: () => void,
    isPreDispatch: boolean
  ) => {
    setLoading(response)
    await query
      .patch(
        isPreDispatch
          ? `/administration/predispatch/${id}`
          : `/administration/dispatch/${response}/${id}`,
        isPreDispatch ? { status: response } : {}
      )
      .then(() => {
        const idx = allDispatches.findIndex((item) => item.id === id)
        const dipatch = allDispatches.find((item) => item.id === id)
        const updated = allDispatches

        const updatedDispacth = {
          ...dipatch,
          status: response,
        } as DispatchItemInterface

        updated.splice(idx, 1, updatedDispacth)
        setAllDispatches(updated)
        toast.success('Modificación exitosa.')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setLoading(undefined)
  }

  const transformDispatchToBill = async (
    dispatchId: number,
    data: Record<string, string | number | boolean | null>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/dispatch/${dispatchId}/billtransform`, data)
      .then(() => {
        const newList = [...allDispatches]
        const idx = newList.findIndex((item) => item.id === dispatchId)
        const currentDispatch = {
          ...newList[idx],
          status: 'BILLED' as DispatchStatus,
        }
        newList.splice(idx, 1, currentDispatch)
        setAllDispatches(newList)
        toast.success('Transformado en factura exitosamente')
        callback()
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const getStockInvestmentReport = async (currencyCode?: string) => {
    setIsLoading(true)
    await query
      .get('/report/stock/inventory', {
        params: { currencyCode: currencyCode },
      })
      .then((resp) => setStockInvestmentReport(resp.data))
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const changeStockReportState = (idx: number, checked: boolean) => {
    if (stockInvestmentReport) {
      const { costCurrency: mainCurrency, result } = stockInvestmentReport
      result.splice(idx, 1, { ...result[idx], active: checked })
      setStockInvestmentReport({ costCurrency: mainCurrency, result })
    }
  }

  const getStockAreaData = async (
    areaId: string,
    filter?: Record<string, string | number | boolean | null>
  ) => {
    setIsLoading(true)
    await Promise.all([
      query.get(
        `/administration/stock/quick-report/${areaId}${generateUrlParams({
          all_data: true,
          ...filter,
        })}`
      ), //Quick Stock Reports
      query.get(
        `/administration/product/area/${areaId}${generateUrlParams(filter)}`
      ),
    ]).then((resp) => {
      setQuickProductReport(resp[0].data)
      setStockProducts(resp[1].data.items)
      setPaginate({
        currentPage: resp[1].data.currentPage,
        totalItems: resp[1].data.totalItems,
        totalPages: resp[1].data.totalPages,
      })
    })
    setIsLoading(false)
  }

  const exportStockProducts = async (
    areaId: string,
    filename: string,
    filter: BasicType,
    callback?: () => void
  ) => {
    const reserve_products_with_prebilling =
      business?.configurationsKey.find(
        (itm) => itm.key === 'reserve_products_with_prebilling'
      )?.value === 'true'
    setIsFetching(true)
    await query
      .get(
        `/administration/product/area/${areaId}${generateUrlParams({
          ...filter,
          all_data: true,
        })}`
      )
      .then((resp) => {
        const stockProd: StockAreaProducts[] = resp.data.items
        const dataToExport: Record<string, string | number>[] = []
        stockProd.map((item) => {
          const price =
            item.product.prices.find((price) => price.isMain) ?? null
          const quantityByGroup = (quantity: number): string => {
            if (item.product.enableGroup) {
              const rest = quantity % (item.product.groupConvertion ?? 1)
              return `${Math.trunc(
                quantity / (item.product.groupConvertion ?? 1)
              )} ${item.product.groupName ?? ''} ${
                rest !== 0
                  ? '(+' + rest + translateMeasure(item.product.measure) + ')'
                  : ''
              }`
            } else return '-'
          }

          const averageCost = new bigDecimal(item.product.averageCost)
          const quantity = new bigDecimal(item.quantity)

          const totalCost = averageCost.multiply(quantity).getValue()

          const objectData: ExportDataObject = {
            Producto: item.product.name,
            Tipo: translateProductTypes(item.product.type),
            'Categoría de venta': item.product.salesCategory?.name,
            'Categoría de almacén': item.product.productCategory?.name,
            Disponibilidad: item.quantity,
            Agrupación: quantityByGroup(item.quantity) ?? '',
            Medida: translateMeasure(item.product.measure),
          }
          if (reserve_products_with_prebilling) {
            objectData['Reservados'] = item?.reserved ?? '-'
          }
          if (allowRoles(['OWNER', 'MANAGER_COST_PRICES'])) {
            objectData[`Costo Unitario en ${business?.costCurrency}`] =
              printPriceWithCommasAndPeriods(item.product.averageCost)
            objectData[`Costo total en ${business?.costCurrency}`] =
              printPriceWithCommasAndPeriods(Number(totalCost))
          }
          if (price) {
            objectData[
              // Precio en
              `${
                business?.priceSystems?.find(
                  (priceSystem) => priceSystem.id === price.priceSystemId
                )?.name ?? ''
              } (${price.codeCurrency ?? '-'})`
            ] = printPriceWithCommasAndPeriods(price.price) ?? '-'
          }
          dataToExport.push(objectData)
        })
        exportExcel(dataToExport, filename)
        if (callback) {
          callback()
        }
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  //-------- AREAS COMPARTIDAS

  const getSharesAreasConfiguration = useCallback(async () => {
    setIsLoading(true)
    await query
      .get('/administration/my-shared-area')
      .then((resp) => {
        setSharesAreasConfiguration(resp.data.rows)
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }, [manageErrors])

  const addShareArea = async (data: BasicType, closeModal: () => void) => {
    setIsFetching(true)

    await query
      .post('/administration/sharedarea', data)
      .then((resp) => {
        setSharesAreasConfiguration([resp.data, ...sharesAreasConfiguration])
        closeModal()
        toast.success('Área compartida correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const deleteShareArea = async (id: string, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/sharedarea/${id}`, {})
      .then(() => {
        setSharesAreasConfiguration(
          sharesAreasConfiguration.filter((item) => item.id !== Number(id))
        )
        callback()
        toast.success('Se eliminó la Cuenta correctamente')
      })
      .catch((error) => manageErrors(error))

    setIsFetching(false)
  }

  const getTotalPendingDispatch = async (businessId: number) => {
    setIsLoading(true)
    try {
      const response = await query.get(
        `/administration/pending-dispatches/${businessId}`
      )
      setPendingDispatchCount(response.data.pending)
    } catch (error) {
      manageErrors(error as unknown as { message: string })
    } finally {
      setIsLoading(false)
    }
  }

  // Función para obtener todos los stock disponibles
  const getAllStockAviables = async (
    filter: BasicType | undefined,
    onSuccess?: (_data: unknown) => void
  ) => {
    setIsLoading(true)
    await query
      .get(`/report/stock/disponibility${generateUrlParams(filter)}`)
      .then((resp) => {
        if (onSuccess) {
          onSuccess(resp.data)
        }
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const getReceiptDocuments = async (buyedreceiptId: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/buyedreceipt/${buyedreceiptId}/documents`)
      .then((resp) => {
        setReceiptDocs(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const postReceiptDocuments = async (
    buyedreceiptId: number,
    payload: ReceiptDocumentsPayload
  ) => {
    setIsFetching(true)
    await query
      .patch(
        `/administration/buyedreceipt/v2/${buyedreceiptId}/documents`,
        payload
      )
      .then(() => {
        toast.success('Actualizado con éxito')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const FindAllOperationalCosts = async (buyedreceiptId: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/buyedreceipt/fixedCost/${buyedreceiptId}`)
      .then((resp) => {
        setAllOperationalCosts(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const NewOperationalCosts = async (
    buyedreceiptId: number,
    data: OperationalCostData,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedreceipt/fixedCost/${buyedreceiptId}`, data)
      .then((resp) => {
        setAllOperationalCosts([resp.data, ...allOperationalCosts])
        toast.success('Agregado exitosamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const DeleteOperationalCosts = async (
    buyedreceiptId: number,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedreceipt/fixedCost/${buyedreceiptId}`, {})
      .then(() => {
        setAllOperationalCosts(
          allOperationalCosts.filter((item) => item.id !== buyedreceiptId)
        )
        toast.success('Eliminado exitosamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const FindAllBatchs = async (buyedreceiptId: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/batch?buyedReceiptId=${buyedreceiptId}`)
      .then((resp) => {
        setAllBatches(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const exportCargo = async (
    receipt: ReceiptBatchInterface,
    business: BusinessInterface
  ) => {
    setIsFetching(true)
    await query
      .get(`/administration/batch?buyedReceiptId=${receipt.id}`)
      .then((resp: { data: { items: BatchItemInterface[] } }) => {
        const receiptProducts = resp?.data?.items

        setAllBatches(receiptProducts)

        generatePdf(
          CargoReportPDF({ receipt, business, receiptProducts }),
          'Carga'
        )
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const NewBatch = async (
    buyedreceiptId: number,
    data: BatchData,
    callBack: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedReceipt/batch/${buyedreceiptId}`, data)
      .then((resp) => {
        setAllBatches([resp.data, ...allBatches])

        toast.success('Agregado correctamente')

        callBack()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const DeleteBatch = async (id: number, callBack: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedReceipt/batch/${id}`, {})
      .then(() => {
        setAllBatches(allBatches.filter((item) => item.id !== id))

        toast.success('Eliminado correctamente')

        if (callBack) callBack()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  //Receipt batch-------
  const getAllReceipt = async (
    filter: Record<string, string | number | boolean | null>
  ) => {
    setIsLoading(true)
    await query
      .get(`/administration/buyedreceipt${generateUrlParams(filter)}`)
      .then((resp) => {
        setReceiptList(resp.data.items)
        setPaginate({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const addReceipt = async (data: ReceiptData, callback: () => void) => {
    setIsFetching(true)
    await query
      .post('/administration/buyedreceipt', data)
      .then((resp) => {
        setReceiptList([resp.data, ...receiptList])
        setPaginate({
          ...paginate,
          totalItems: resp.data.totalItems,
        } as PaginateInterface)
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const addReceiptV2 = async (
    data: Record<string, unknown>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .post('/administration/buyedreceipt/v2', data)
      .then((resp) => {
        setReceiptList([resp.data, ...receiptList])
        setPaginate({
          ...paginate,
          totalItems: resp.data.totalItems,
        } as PaginateInterface)
        toast.success('Carga agregada exitosamente')
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const getReceipt = async (id: number) => {
    setIsLoading(true)
    await query
      .get(`/administration/buyedreceipt/${id}`)
      .then((resp) => setReceipt(resp.data))
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const findAllReceiptPayments = async (id: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/buyedreceipt/${id}/payments`)
      .then((resp) => {
        setReceiptPayments(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const NewReceiptPayment = async (
    id: number,
    data: {
      amount: number
      codeCurrency: string
      type: string
      observations: string
      accountId: number
      supplierId?: number //Optional
    },
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedreceipt/${id}/payments`, data)
      .then((resp) => {
        setReceiptPayments([
          {
            createdAt: moment().format(),
            ...resp.data[0],
          },
          ...(receiptPayments ?? []),
        ])

        toast.success('Pago agregado correctamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const UpdateReceiptPayment = async (
    id: number,
    data: {
      type: string
      observations: string
      supplierId?: number //Optional
    },
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/buyedreceipt/payments/${id}`, data)
      .then((resp) => {
        setReceiptPayments(
          receiptPayments?.map((receiptPayment) => {
            if (receiptPayment.id === resp.data.id) {
              return resp.data
            } else {
              return receiptPayment
            }
          }) ?? []
        )

        toast.success('Pago editado correctamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const DeleteReceiptPayment = async (id: number, closeModal: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedreceipt/payments/${id}`, {})
      .then(() => {
        setReceiptPayments(
          receiptPayments?.filter((item) => item.id !== id) ?? []
        )
        toast.success('Pago eliminado correctamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const findAllProductMovements = async (id: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/buyedreceipt/${id}/movements`)
      .then((resp) => {
        setproductMovements(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const NewProductMovements = async (
    id: number,
    data: ProductMovementData,
    closeModal: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedreceipt/${id}/movements`, data)
      .then((resp) => {
        setproductMovements([
          {
            createdAt: moment().format(),
            ...resp.data,
          },
          ...(productMovements ?? []),
        ])
        toast.success('Movimiento agregado exitosamente')
        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const DeleteProductMovements = async (id: number, closeModal: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedreceipt/movements/${id}`, {})
      .then(() => {
        setproductMovements(
          productMovements?.filter((item) => item.id !== id) ?? []
        )

        toast.success('Movimiento eliminado exitosamente')

        closeModal()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const updateReceipt = async (
    id: number,
    data: Record<string, unknown>,
    close: () => void,
    updateOuterList: (_id: number, _data: unknown) => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/buyedreceipt/${id}`, data)
      .then((resp) => {
        setReceipt(resp.data)
        setReceiptList(
          receiptList.map((item) => {
            if (item.id === id) {
              return resp.data
            } else {
              return item
            }
          })
        )
        close()
        toast.success('Modificado exitosamente')
        updateOuterList(id, resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const dispatchReceipt = async (
    receiptId: number,
    data: Record<string, unknown>,
    callback?: (_status: string) => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedreceipt/newdispatch/${receiptId}`, data)
      .then((resp) => {
        setReceipt({
          ...receipt,
          status: resp.data.status,
          dispatch: {
            createdAt: resp.data.createdAt,
            id: resp.data.id,
            stockAreaTo: resp.data.stockAreaTo,
            status: resp.data.status,
          },
        } as ReceiptBatchInterface)
        if (callback) {
          callback(resp.data.status)
        }
        toast.success('Despacho realizado con éxito')
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const extractFoundsFrom = async (
    receiptId: number,
    data: Record<string, unknown>,
    callback?: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedreceipt/movetoaccount/${receiptId}`, data)
      .then(() => {
        if (callback) {
          callback()
        }
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const addBatch = async (
    receiptId: number,
    data: Record<string, unknown>,
    callback?: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/administration/buyedreceipt/batch/${receiptId}`, data)
      .then((resp) => {
        const batches = [resp.data, ...(receipt?.batches ?? [])]
        setReceipt({ ...receipt, batches } as ReceiptBatchInterface)
        if (callback) {
          callback()
        }
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const updateBatch = async (
    batchId: number,
    data: Record<string, unknown>,
    callback?: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/buyedreceipt/batch/${batchId}`, data)
      .then((resp) => {
        const batches = receipt?.batches ?? []
        const idx = batches?.findIndex((elem) => elem.id === batchId)
        batches?.splice(idx, 1, resp.data)
        setReceipt({ ...receipt, batches } as ReceiptBatchInterface)
        setAllBatches(
          allBatches?.map((item) => {
            if (item.id === batchId) {
              return resp.data
            } else {
              return item
            }
          }) ?? []
        )

        toast.success('Modificado correctamente.')
        if (callback) {
          callback()
        }
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const deleteBatch = async (batchId: number, callback?: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedreceipt/batch/${batchId}`, {})
      .then(() => {
        const batches = receipt?.batches ?? []
        const idx = batches?.findIndex((item) => item.id === batchId)
        batches?.splice(idx, 1)
        setReceipt({ ...receipt, batches } as ReceiptBatchInterface)
        if (callback) {
          callback()
        }
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const cancelReceipt = async (id: number, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedreceipt/${id}`, {})
      .then(() => {
        setReceipt({ ...receipt, status: 'CANCELLED' } as ReceiptBatchInterface)
        toast.success('Cancelado exitosamente')
        callback()
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const updateReceiptsLocally = (
    id: number,
    data: Partial<ReceiptBatchInterface>
  ) => {
    const idx = receiptList.findIndex((elem) => elem.id === id)
    const newList = [...receiptList]
    newList.splice(idx, 1, { ...newList[idx], ...data })
    setReceiptList(newList)
  }

  const editAreasToRequest = async (
    areaId: number,
    data: Record<string, unknown>
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/area/${areaId}/allowedtodispatch`, data)
      .then(() => {
        toast.success('Modificado exitosamente')
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const editAreasForRequest = async (
    areaId: number,
    data: Record<string, unknown>
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/area/${areaId}/allowedfordispatch`, data)
      .then(() => {
        toast.success('Modificado exitosamente')
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const editAreas = async (areaId: number, data: Record<string, unknown>) => {
    setIsFetching(true)
    await query
      .patch(`/administration/area/${areaId}`, data)
      .then(() => {
        toast.success('Modificado exitosamente')
      })
      .catch(manageErrors)
    setIsFetching(false)
  }

  const findAllStatusDates = async (id: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/buyedreceipt/statusdates/${id}`)
      .then((resp) => {
        setAllStatusDates(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const deleteStatusDates = async (id: number, callBack: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/administration/buyedreceipt/statusdates/${id}`, {})
      .then(() => {
        setAllStatusDates(
          allStatusDates?.filter((item) => item.id !== id) ?? []
        )
        toast.success('Eliminado correctamente.')
        callBack()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const updateStatusDates = async (
    id: number,
    data: StatusDateData,
    callBack: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/administration/buyedreceipt/statusdates/${id}`, data)
      .then(() => {
        const { registeredAt } = data

        setAllStatusDates(
          allStatusDates?.map((item) => {
            if (item.id === id) {
              return {
                ...item,
                registeredAt,
              }
            } else {
              return item
            }
          }) ?? []
        )

        callBack()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const getSummarizedPayments = async (id: number) => {
    setIsFetching(true)
    await query
      .get(`/administration/buyedreceipt/${id}/payments/summarize`)
      .then((resp) => {
        setSummaraizedPayments(resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const getAllBusinessAreas = async () => {
    let businessAreas: AreasInterface[] = []
    await query
      .get('/administration/area/?inAllMyBusiness=true')
      .then((resp) => {
        businessAreas = resp.data.items
      })
      .catch((e) => {
        manageErrors(e)
      })
    return businessAreas
  }

  const createDispatch = async (payload: CreateDispatchPayload) => {
    setIsFetching(true)
    await query
      .post('/administration/dispatch/movement', payload)
      .then((resp) => {
        setAllDispatches([resp.data, ...allDispatches])
        // Show success message based on dispatch type
        const message = payload.isPreDispatch
          ? 'Predespacho creado exitosamente'
          : 'Despacho creado exitosamente'
        toast.success(message)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  return {
    isLoading,
    isFetching,
    paginate,
    allAreas,
    areaAllFilter,
    sharesAreas,
    addShareArea,
    sharesAreasConfiguration,
    area,
    stockProducts,
    movement,
    inventory,
    allInventories,
    allMovements,
    allDispatches,
    despacho,
    areaResources,
    stockInvestmentReport,
    quickProductReport,
    pendingDispatchCount,
    getAllAreas,
    getAreaAllFinder,
    getArea,
    addArea,
    updateArea,
    deleteArea,
    addMovement,
    getMovementByArea,
    getMovement,
    getInventories,
    getInventory,
    deleteMovements,
    deleteStockMovement,
    addDispatch,
    getAllDispatches,
    getAllDispatchesV2,
    getDispatch,
    acceptDispatch,
    rejectDispatch,
    acceptPreDispatch,
    rejectPreDispatch,
    responseDispatch,
    transformDispatchToBill,
    getSharesAreas,
    getProductsByArea,
    getAreaResources,
    updateAreaResource,
    deleteAreaResource,
    addAreaResource,
    updateStockProductState,
    getStockInvestmentReport,
    changeStockReportState,
    getStockAreaData,
    exportStockProducts,
    getSharesAreasConfiguration,
    deleteShareArea,
    getAreasToDispatch,
    getTotalPendingDispatch,
    receiptList,
    getAllReceipt,
    getReceipt,
    addReceipt,
    addReceiptV2,
    receiptPayments,
    findAllReceiptPayments,
    updateReceipt,
    receipt,
    updateReceiptsLocally,
    dispatchReceipt,
    extractFoundsFrom,
    updateBatch,
    addBatch,
    deleteBatch,
    cancelReceipt,
    editAreasToRequest,
    editAreasForRequest,
    editAreas,
    NewReceiptPayment,
    DeleteReceiptPayment,
    productMovements,
    findAllProductMovements,
    NewProductMovements,
    DeleteProductMovements,
    FindAllBatchs,
    exportCargo,
    allBatches,
    FindAllOperationalCosts,
    allOperationalCosts,
    NewOperationalCosts,
    DeleteOperationalCosts,
    NewBatch,
    DeleteBatch,
    getReceiptDocuments,
    receiptDocs,
    setReceiptDocs,
    addDispatchAndUpdatePreDispatchState,
    postReceiptDocuments,
    findAllStatusDates,
    allStatusDates,
    deleteStatusDates,
    updateStatusDates,
    getSummarizedPayments,
    summaraizedPayments,
    UpdateReceiptPayment,
    loading,
    getAllStockAviables,
    getAllBusinessAreas,
    createDispatch,
  }
}

export default useServerArea
